import { useMemo, useState } from 'react';
import { Search, Star } from 'lucide-react';
import { Modal } from './Modal';

/**
 * v0.4後半 月次実績一括入力 第2フェーズ I-4b (§6-1 D-993 / §6-2 項目3): 月次入力「1日分のシート」の
 * 「勤務店舗 [変更]」用の単一選択店舗ピッカー。D-993 (本人入力の店舗制限緩和) により self でも
 * 所属外の店舗を選べるため、候補は呼び出し元がテナント全体の店舗一覧 (`GET /nodes` を
 * `node_type_code==='store'` で絞ったもの、`useScopeStore.roots` と同じデータソース) をそのまま渡す
 * 前提とする (nodes/all スコープの代理入力で権限外店舗を選んだ場合は、提出時にその日だけ
 * FORBIDDEN スキップされ既存の describeSkipped で理由が表示される — 事前のクライアント側絞り込みは
 * 行わない簡略化、§6 方針「過剰実装を避ける」)。
 *
 * ⚠️ 設計書 §3-6 の `monthly_entry_node_picker_mode` (list/search_dialog をテナント設定で切替) は
 * 今回未実装 (残課題): 常にこの「検索欄+一覧」の1種類のUIで統一する (店舗数の多寡に関わらず動作する
 * ため、実害は無いと判断)。次にこの画面へ触るセッションで設定を作る場合はここに分岐を追加する。
 */
export type StoreOption = { nodeId: string; name: string };

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (nodeId: string) => void;
  options: StoreOption[];
  homeNodeId: string | null;
  recentNodeIds: string[];
};

const normalize = (s: string) => s.toLowerCase().replace(/[\s　]+/g, '');

export function StoreSinglePicker({ open, onClose, onSelect, options, homeNodeId, recentNodeIds }: Props) {
  const [query, setQuery] = useState('');

  const ordered = useMemo(() => {
    const byId = new Map(options.map((o) => [o.nodeId, o]));
    const home = homeNodeId ? byId.get(homeNodeId) : undefined;
    const recents = recentNodeIds.map((id) => byId.get(id)).filter((o): o is StoreOption => !!o && o.nodeId !== homeNodeId);
    const usedIds = new Set([home?.nodeId, ...recents.map((o) => o.nodeId)].filter((v): v is string => !!v));
    const rest = options.filter((o) => !usedIds.has(o.nodeId)).sort((a, b) => a.name.localeCompare(b.name, 'ja'));
    return { home, recents, rest };
  }, [options, homeNodeId, recentNodeIds]);

  const q = normalize(query);
  const filterOpt = (o: StoreOption) => q === '' || normalize(o.name).includes(q);

  const rows: Array<{ option: StoreOption; badge: string | null }> = [
    ...(ordered.home && filterOpt(ordered.home) ? [{ option: ordered.home, badge: 'いつもの店舗' }] : []),
    ...ordered.recents.filter(filterOpt).map((option) => ({ option, badge: '最近使った店舗' })),
    ...ordered.rest.filter(filterOpt).map((option) => ({ option, badge: null })),
  ];

  return (
    <Modal open={open} onClose={onClose} title="勤務店舗を選ぶ" size="sm">
      <div className="space-y-3">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-sub" />
          <input
            type="text" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="店舗名で検索" autoFocus
            className="w-full min-h-[44px] pl-8 pr-2 py-2 rounded border border-surface-border focus-ring text-sm"
          />
        </div>
        <div className="max-h-[50vh] overflow-y-auto -mx-1 px-1" role="listbox" aria-label="店舗一覧">
          {rows.length === 0 && <p className="text-xs text-text-sub px-2 py-3">該当する店舗がありません</p>}
          {rows.map(({ option, badge }) => (
            <button
              key={option.nodeId} type="button" role="option"
              onClick={() => { onSelect(option.nodeId); onClose(); }}
              className="w-full min-h-[44px] flex items-center justify-between gap-2 px-2 py-2 rounded hover:bg-surface-sunken text-left text-sm"
            >
              <span className="truncate">{option.name}</span>
              {badge && (
                <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-text-sub">
                  {badge === 'いつもの店舗' && <Star size={11} className="shrink-0" />}
                  {badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}
